import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Tv, Radio, Loader2, CheckCircle2, PlusCircle, Upload, Image as ImageIcon, ExternalLink } from "lucide-react";
import CropModal from "@/components/CropModal";

export default function RegisterMedia() {
  const navigate = useNavigate();
  const [provinces, setProvinces] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [done, setDone] = useState(false);
  const [doneMsg, setDoneMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoCrop, setLogoCrop] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [bannerCrop, setBannerCrop] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [f, setF] = useState({ type: "tv", name: "", manager_name: "", email: "", password: "", phone: "", province: "", city: "", address: "", description: "", website: "", stream_url: "", logo: "", banner: "", languages: [], categories: [] });

  useEffect(() => {
    api.get("/provinces").then((r) => setProvinces(r.data));
    api.get("/languages").then((r) => setLanguages(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const uploadRegLogo = async (fileObj) => {
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await api.post("/media/register/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo", data.url);
      toast.success("Logo ajouté");
    } catch (e) { toast.error(apiErr(e)); } finally { setLogoUploading(false); }
  };

  const uploadRegBanner = async (fileObj) => {
    setBannerUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await api.post("/media/register/banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("banner", data.url);
      toast.success("Image d'accueil ajoutée");
    } catch (e) { toast.error(apiErr(e)); } finally { setBannerUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const { data } = await api.post("/media/register", f); setDoneMsg(data.message || ""); setDone(true); }
    catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  if (done) return (
    <div className="max-w-lg mx-auto px-4 text-center py-24">
      <CheckCircle2 className="mx-auto text-emerald-500 mb-4" size={56} />
      <h1 className="font-heading font-extrabold text-2xl">Demande envoyée !</h1>
      <p className="text-muted-foreground mt-2">{doneMsg || "Votre demande d'inscription a bien été reçue. Elle sera examinée et validée par l'administration du portail."}</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
        <Button onClick={() => navigate("/manager/login")} className="font-heading font-bold" data-testid="register-media-login">Se connecter</Button>
        <Button onClick={() => navigate("/")} variant="outline" data-testid="register-media-home">Retour à l'accueil</Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 mt-6">
      <div className="flex items-center gap-2 mb-1"><PlusCircle className="text-primary" /><h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Inscrire mon média</h1></div>
      <p className="text-sm text-muted-foreground mb-6">Rejoignez le portail national des télévisions et radios de la RDC.</p>

      <form onSubmit={submit} className="space-y-4 rounded-2xl border border-border bg-card p-6">
        <div className="grid grid-cols-2 gap-3">
          {[["tv", "Télévision", Tv], ["radio", "Radio", Radio]].map(([v, l, Icon]) => (
            <button key={v} type="button" onClick={() => set("type", v)} data-testid={`regmedia-type-${v}`}
              className={`flex items-center justify-center gap-2 rounded-xl border-2 py-4 font-heading font-bold transition-all ${f.type === v ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"}`}>
              <Icon size={20} /> {l}
            </button>
          ))}
        </div>

        <Input placeholder="Nom du média *" value={f.name} onChange={(e) => set("name", e.target.value)} required data-testid="regmedia-name" />

        <div className="flex items-center gap-3">
          <span className="w-16 h-16 rounded-full border border-border bg-white overflow-hidden flex items-center justify-center shrink-0">
            {f.logo ? <img src={f.logo} className="w-full h-full object-cover" alt="logo" /> : <ImageIcon size={20} className="text-muted-foreground" />}
          </span>
          <div>
            <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-sm font-medium hover:border-primary/50" data-testid="regmedia-logo-btn">
              {logoUploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {f.logo ? "Changer le logo" : "Ajouter un logo"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) setLogoCrop(file); e.target.value = ""; }} />
            </label>
            <p className="text-[11px] text-muted-foreground mt-1">Recadrage carré, aperçu avant envoi. Optionnel.</p>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Image d'accueil (bannière)</label>
          <div className="rounded-xl border border-border overflow-hidden bg-secondary/40 aspect-[16/9] flex items-center justify-center relative" data-testid="regmedia-banner-preview">
            {f.banner ? <img src={f.banner} className="w-full h-full object-cover" alt="bannière" /> : <ImageIcon size={28} className="text-muted-foreground" />}
          </div>
          <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-sm font-medium hover:border-primary/50 mt-2" data-testid="regmedia-banner-btn">
            {bannerUploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {f.banner ? "Changer l'image d'accueil" : "Ajouter une image d'accueil"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) setBannerCrop(file); e.target.value = ""; }} />
          </label>
          <p className="text-[11px] text-muted-foreground mt-1">Format paysage 16:9. Affichée en tête de votre page. Optionnel.</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Responsable *" value={f.manager_name} onChange={(e) => set("manager_name", e.target.value)} required data-testid="regmedia-manager" />
          <Input type="email" placeholder="Email *" value={f.email} onChange={(e) => set("email", e.target.value)} required data-testid="regmedia-email" />
        </div>
        <div>
          <Input type="password" placeholder="Mot de passe (min. 6 caractères) *" value={f.password} onChange={(e) => set("password", e.target.value)} required minLength={6} data-testid="regmedia-password" />
          <p className="text-[11px] text-muted-foreground mt-1.5">Ces identifiants (email + mot de passe) vous permettront de vous connecter et de gérer votre média une fois validé. Si cet email possède déjà un compte, utilisez votre mot de passe habituel.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Téléphone *" value={f.phone} onChange={(e) => set("phone", e.target.value)} required data-testid="regmedia-phone" />
          <Select value={f.province} onValueChange={(v) => set("province", v)}>
            <SelectTrigger data-testid="regmedia-province"><SelectValue placeholder="Province *" /></SelectTrigger>
            <SelectContent>{provinces.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Ville" value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="regmedia-city" />
          <Input placeholder="Adresse" value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="regmedia-address" />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Langues</label>
          <div className="flex flex-wrap gap-2">
            {languages.map((l) => (
              <button key={l.id} type="button" onClick={() => toggle("languages", l.name)} data-testid={`regmedia-lang-${l.slug}`}
                className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${f.languages.includes(l.name) ? "bg-primary text-white border-primary" : "border-border hover:border-primary/40"}`}>{l.name}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Catégories</label>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c.id} type="button" onClick={() => toggle("categories", c.name)} data-testid={`regmedia-cat-${c.slug}`}
                className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${f.categories.includes(c.name) ? "bg-primary text-white border-primary" : "border-border hover:border-primary/40"}`}>{c.name}</button>
            ))}
          </div>
        </div>

        <Textarea placeholder="Description du média" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="regmedia-description" />
        <Input placeholder="Site web (https://…)" value={f.website} onChange={(e) => set("website", e.target.value)} data-testid="regmedia-website" />
        <Input placeholder={f.type === "tv" ? "URL du flux HLS (.m3u8)" : "URL du flux audio"} value={f.stream_url} onChange={(e) => set("stream_url", e.target.value)} data-testid="regmedia-stream" />
        <div className="rounded-xl border border-rdcblue/30 bg-rdcblue/5 px-3.5 py-3 -mt-1" data-testid="regmedia-ricast-hint">
          <p className="text-[13px] text-muted-foreground leading-relaxed mb-2.5">
            Vous n'avez pas encore d'URL de flux ? Créez votre diffusion en direct sur RiCast, puis collez ici l'URL obtenue.
          </p>
          <a href="https://rivivoo.com/cast" target="_blank" rel="noreferrer" data-testid="regmedia-ricast-link"
            className="group inline-flex items-center gap-2.5 rounded-full border border-rdcred/40 bg-white px-4 py-2 font-heading font-bold text-sm text-rdcred shadow-sm transition-all hover:border-rdcred hover:shadow-md hover:-translate-y-0.5">
            <img src="/ricast-icon.png" alt="RiCast" className="w-6 h-6 rounded-md" />
            Créer mon flux sur RiCast
            <ExternalLink size={14} className="opacity-70 group-hover:opacity-100" />
          </a>
        </div>

        <Button type="submit" disabled={loading} className="w-full h-12 font-heading font-bold" data-testid="regmedia-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Envoyer la demande"}
        </Button>
      </form>

      {logoCrop && (
        <CropModal file={logoCrop} kind="logo" onCancel={() => setLogoCrop(null)}
          onDone={async (blob) => { setLogoCrop(null); await uploadRegLogo(blob); }} />
      )}
      {bannerCrop && (
        <CropModal file={bannerCrop} kind="banner" onCancel={() => setBannerCrop(null)}
          onDone={async (blob) => { setBannerCrop(null); await uploadRegBanner(blob); }} />
      )}
    </div>
  );
}
